// src/shell/AppShell.tsx
import React, { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ToastHost from '../components/ToastHost';
import AppFooter from '../components/AppFooter';
import RewardHost from '../components/RewardHost';
import ConfettiBurst from '../components/ConfettiBurst';
import { MissionStatsProvider } from '../components/StreakBadge';
import { clearSessionPerms, decidePerm } from '../lib/permPrefs';
import { setLastRoute, getActivePhone, getLastRoute } from '../lib/profileStore';
import { getBootCache, getBootState } from '../lib/bootCoordinator';
import { emit, on } from '../lib/events';
import { ensureFirebaseBoot } from '../lib/firebaseBoot';
import { toast } from '../store/toast';
import { track } from '../lib/track';
import TourOverlay from '../components/TourOverlay';
import { shouldAutoStartTour, useTour } from '../store/tour';
import OfflineBanner from '../components/OfflineBanner';
import OfflineTooltip from '../components/OfflineTooltip';
import { addNotice } from '../lib/notifications';
import { clearVolatileAuthState, rememberKnownAccount, syncSessionFromAuth } from '../lib/session';
import useCart from '../store/cart';
import { addCoins } from '../lib/api';
const lazyCloud = () => import('../lib/cloudProfile');
const lazyTokens = () => import('../lib/tokens');
const lazyNotify = () => import('../lib/notify');
const lazyReturn = () => import('../lib/orderReturn');
import BannerHost from '../components/BannerHost';
import RouteTransition from '../components/RouteTransition';
import { log, now } from '../lib/devLog';
import { clearHomeSnapshots } from '../lib/homeSnapshot';
let authMod: Promise<typeof import('firebase/auth')> | null = null;
const loadAuth = () => {
  if (!authMod) authMod = import('firebase/auth');
  return authMod;
};
const ReturnBanner = React.lazy(() => import('../components/ReturnBanner'));

// Consumer app default: keep sessions sticky and only soft-force re-entry after long inactivity.
const IDLE_MINUTES = Number(import.meta.env.VITE_IDLE_MINUTES || 720);
const IDLE_MS = Number.isFinite(IDLE_MINUTES) && IDLE_MINUTES > 0
  ? Math.round(IDLE_MINUTES * 60 * 1000)
  : 0;
const ALLOW_DUMMY_FEED = import.meta.env.DEV && import.meta.env.VITE_ALLOW_DUMMY_FEED === '1';
const FEED_KIND: 'crawl' | 'dummy' = (() => {
  const raw = String(import.meta.env.VITE_FEED || '').trim().toLowerCase();
  if (raw === 'dummy' && ALLOW_DUMMY_FEED) return 'dummy';
  return 'crawl';
})();
const RAW_BUILD_ID =
  import.meta.env.VITE_COMMIT_SHA ||
  import.meta.env.VITE_BUILD_ID ||
  '';
const BUILD_ID = RAW_BUILD_ID && !RAW_BUILD_ID.startsWith('$') ? RAW_BUILD_ID : '';
const IMPORT_META_FINGERPRINT = (() => {
  try {
    const raw = String(import.meta.url || '').trim();
    if (!raw) return '';
    const tail = raw.split('/').pop() || raw;
    return tail.replace(/[^a-z0-9._-]+/gi, '_') || raw;
  } catch {
    return '';
  }
})();
const APP_VERSION = (() => {
  if (BUILD_ID) return BUILD_ID;
  if (IMPORT_META_FINGERPRINT) return IMPORT_META_FINGERPRINT;
  if (!import.meta.env.PROD) return 'v4';
  try {
    const key = 'bw.build.fallback';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const ts = String(Date.now());
    localStorage.setItem(key, ts);
    console.error('[build] missing VITE_COMMIT_SHA/VITE_BUILD_ID; using fallback', ts);
    return ts;
  } catch {
    const ts = String(Date.now());
    console.error('[build] missing VITE_COMMIT_SHA/VITE_BUILD_ID; using fallback', ts);
    return ts;
  }
})();
const SW_URL = `/pwa-sw.js?v=${encodeURIComponent(APP_VERSION)}`;
const LOGIN_STREAK_REWARD = 5;

function daysBetween(a: Date, b: Date) {
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.floor(Math.abs(utc2 - utc1) / (24 * 60 * 60 * 1000));
}

function isCustomerRewardRoute(pathname = typeof window !== 'undefined' ? window.location.pathname : '/') {
  const path = String(pathname || '/');
  if (isOperationalRoute(path)) return false;
  return true;
}

function isOperationalRoute(pathname = typeof window !== 'undefined' ? window.location.pathname : '/') {
  const path = String(pathname || '/');
  return (
    path === '/' ||
    path === '/rasa' ||
    path.startsWith('/rasa/') ||
    path === '/rasoi' ||
    path === '/gaur_cafe' ||
    path === '/chandafood' ||
    path === '/networthycafe' ||
    path === '/saleems' ||
    path.startsWith('/order/table') ||
    path.startsWith('/order/rasa') ||
    path.startsWith('/admin') ||
    path.startsWith('/merchant') ||
    path === '/restaurant' ||
    path.startsWith('/restaurant/login') ||
    path.startsWith('/restaurant/onboarding') ||
    path.startsWith('/restaurant/portal')
  );
}

async function importPendingPushNotices() {
  if (typeof indexedDB === 'undefined') return;
  try {
    const req = indexedDB.open('bw_push', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('pending')) {
        db.createObjectStore('pending', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction('pending', 'readwrite');
      const store = tx.objectStore('pending');
      const getAll = store.getAll();
      getAll.onsuccess = () => {
        const list = Array.isArray(getAll.result) ? getAll.result : [];
        list.forEach((n: any) => {
          if (!n?.kind || !n?.title) return;
          addNotice({
            id: n.id,
            kind: n.kind,
            title: n.title,
            body: n.body,
            ts: typeof n.ts === 'number' ? n.ts : Date.now(),
            source: n.source === 'details' || n.source === 'system' ? n.source : undefined,
          });
        });
        store.clear();
      };
      tx.oncomplete = () => db.close();
    };
  } catch {
    /* ignore */
  }
}

export default function AppShell() {
  const nav = useNavigate();
  const location = useLocation();
  const timerRef = useRef<number | null>(null);
  const renderIdRef = useRef(0);
  renderIdRef.current += 1;
  log('[ui] AppShell render', {
    renderId: renderIdRef.current,
    pathname: location.pathname,
    ts: now(),
  });
  const [offline, setOffline] = React.useState<boolean>(() => {
    if (typeof navigator === 'undefined') return false;
    return navigator.onLine === false;
  });
  const [bootReady, setBootReady] = React.useState<boolean>(() => {
    const cached = getBootCache();
    return Boolean(cached?.ready);
  });
  const [updateReady, setUpdateReady] = React.useState(false);
  const [authSettled, setAuthSettled] = React.useState(false);
  const [authedUserExists, setAuthedUserExists] = React.useState(false);
  const hideTimestampRef = React.useRef<number | null>(null);
  const streakPingKeyRef = React.useRef<string | null>(null);
  const loginStreakKeyRef = React.useRef<string | null>(null);
  const swRegRef = React.useRef<ServiceWorkerRegistration | null>(null);
  const cartSyncTimerRef = React.useRef<number | null>(null);
  const cartSyncHashRef = React.useRef<string>('');
  const cartItems = useCart((s) => s.items);
  const swReloadedRef = React.useRef(false);
  const hydratedRef = React.useRef(false);
  const lastAuthUidRef = React.useRef<string | null>(null);

  // Eagerly boot Firebase once for the app shell to avoid race conditions (analytics/auth)
  useEffect(() => {
    ensureFirebaseBoot().catch(() => {});
    void importPendingPushNotices();
  }, [nav]);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      getBootState()
        .then((state) => {
          if (cancelled) return;
          setBootReady(Boolean(state?.ready));
        })
        .catch(() => {});
    };
    refresh();
    const onInvalidate = () => {
      setBootReady(false);
      refresh();
    };
    window.addEventListener('bw:boot:invalidated', onInvalidate as any);
    return () => {
      cancelled = true;
      window.removeEventListener('bw:boot:invalidated', onInvalidate as any);
    };
  }, []);

  // Reload if the tab was hidden for >30 minutes
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        hideTimestampRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const last = hideTimestampRef.current;
        if (last != null && Date.now() - last > 30 * 60 * 1000) {
          window.location.reload();
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // One-time cleanup of legacy session flags to avoid skipping OTP
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const MIGRATION_FLAG = 'bw.migration.v1.clearedStaleAuth';
    if (localStorage.getItem(MIGRATION_FLAG)) return;
    try {
      sessionStorage.removeItem('bw.session.phone');
      sessionStorage.removeItem('bw.active.phone');
      sessionStorage.removeItem('bw.session.phoneVerified'); // will be set after OTP
    } catch {}
    // Best-effort sign out stale Firebase session
    import('firebase/auth')
      .then(({ getAuth, signOut }) => {
        try { signOut(getAuth()).catch(() => {}); } catch {}
      })
      .catch(() => {});
    try { localStorage.setItem(MIGRATION_FLAG, '1'); } catch {}
  }, []);

  // One-time cleanup of known stale cache keys on version changes (safe list only)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const VERSION_KEY = 'bw.app.version';
    const prev = localStorage.getItem(VERSION_KEY);
    if (prev === APP_VERSION) return;
    try {
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i) || '';
        if (key.startsWith('bw.dishes.cache.')) localStorage.removeItem(key);
        if (key === 'bw_theme' || key === 'bw.theme') localStorage.removeItem(key);
        if (key === 'bw.notifications.inbox') localStorage.removeItem(key);
        if (key.startsWith('bw.ui.')) localStorage.removeItem(key);
      }
      try {
        sessionStorage.removeItem('bw.home.dishes');
        sessionStorage.removeItem('bw.home.dishes.baseline');
        sessionStorage.removeItem('bw.home.pools');
        sessionStorage.removeItem('bw.home.dishes.v2');
        sessionStorage.removeItem('bw.home.dishes.baseline.v2');
        sessionStorage.removeItem('bw.home.pools.v2');
        sessionStorage.removeItem('bw.availability.cache');
        sessionStorage.removeItem('bw.availability.lastDishes');
      } catch {
        /* ignore */
      }
      void clearHomeSnapshots();
      localStorage.setItem(VERSION_KEY, APP_VERSION);
    } catch {
      /* ignore */
    }
  }, []);

  // Migrate legacy microphone keys to the canonical permPrefs.microphone storage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem('bw:permPrefs:v1');
      if (raw && raw.includes('"mic"')) {
        const parsed = JSON.parse(raw || '{}') || {};
        if (parsed.mic && !parsed.microphone) {
          parsed.microphone = parsed.mic;
          delete parsed.mic;
          localStorage.setItem('bw:permPrefs:v1', JSON.stringify(parsed));
        }
      }
      if (sessionStorage.getItem('bw:permSession:mic') === '1') {
        sessionStorage.removeItem('bw:permSession:mic');
        sessionStorage.setItem('bw:permSession:microphone', '1');
      }
    } catch {
      /* ignore */
    }
  }, []);

  // App open + daily session ping
  useEffect(() => {
    try {
      track('app_open');
      const today = new Date().toISOString().slice(0, 10);
      const last = localStorage.getItem('bw.session.ping');
      if (last !== today) {
        localStorage.setItem('bw.session.ping', today);
        track('session_daily_ping', { date: today });
      }

      // Retention markers (install date based)
      const install = localStorage.getItem('bw.install.date') || today;
      localStorage.setItem('bw.install.date', install);
      const daysSince = Math.floor(
        (new Date(today).getTime() - new Date(install).getTime()) / 86400000
      );
      if (daysSince === 1) track('day1_return');
      if (daysSince === 7) track('day7_return');
    } catch {}
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const { getAuth, onAuthStateChanged } = await loadAuth();
        const auth = getAuth();
        const fire = async (user: any) => {
          if (!user || cancelled) return;
          if (!isCustomerRewardRoute()) return;
          const today = new Date().toISOString().slice(0, 10);
          const key = `bw.push.tasks.day:${user.uid}`;
          if (localStorage.getItem(key) === today) return;
          const notify = await lazyNotify();
          if (typeof notify.sendTasksReminder !== 'function') return;
          await notify.sendTasksReminder();
          localStorage.setItem(key, today);
        };
        if (auth.currentUser) {
          await fire(auth.currentUser);
          return;
        }
        const unsub = onAuthStateChanged(auth, (u) => {
          if (!u) return;
          void fire(u).catch(() => {});
          unsub();
        });
      } catch {}
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    try {
      const mode = localStorage.getItem('bw.push.mode');
      if (mode !== 'temp') return;
      const ts = Number(localStorage.getItem('bw.push.tempEnabledAt') || 0);
      const expired = !ts || Date.now() - ts > 24 * 60 * 60 * 1000;
      if (!expired) return;
      localStorage.setItem('bw.push.mode', 'never');
      localStorage.removeItem('bw.push.tempEnabledAt');
      if (import.meta.env.DEV) {
        console.debug('[push] temp expired; disabling');
      }
      void lazyNotify().then((m) => m.disablePush?.());
    } catch {}
  }, []);

  // Clear session flags only on a real sign-out transition (user -> null after being authed)
  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const boot = async () => {
      try {
        const { getAuth, onAuthStateChanged } = await loadAuth();
        const auth = getAuth();
        unsub = onAuthStateChanged(auth, (user) => {
          if (cancelled) return;
          setAuthSettled(true);
          setAuthedUserExists(!!user?.uid);
          if (user?.uid) {
            lastAuthUidRef.current = user.uid;
            void syncSessionFromAuth(user, { log: import.meta.env.DEV });
            return;
          }
          if (lastAuthUidRef.current) {
            lastAuthUidRef.current = null;
            try {
              sessionStorage.removeItem('bw.session.phoneVerified');
              sessionStorage.removeItem('bw.auth.verified');
            } catch {}
          }
        });
      } catch {}
    };
    boot();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  // If no verified session but passkey hint exists, route to quick unlock
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!authSettled) return;
        if (authedUserExists) return;
        const hasSession = !!sessionStorage.getItem('bw.session.phoneVerified');
        const hasPasskey = localStorage.getItem('bw:hasPasskey') === 'true';
        const lastPhone = localStorage.getItem('bw:lastUserPhone') || localStorage.getItem('bw.lastPhone');
        const justUnlockedAt = Number(localStorage.getItem('bw.justUnlockedAt') || 0);
        const logoutReason = sessionStorage.getItem('bw.logoutReason');
        const path = location.pathname;
        const authContext =
          path === '/' ||
      path === '/explore' ||
      path.startsWith('/legal/') ||
          path === '/unlock' ||
          path.startsWith('/auth') ||
          path.startsWith('/onboarding') ||
          path === '/quick-unlock';
        if (isOperationalRoute(path)) return;
        if (path === '/quick-unlock') return;
        if (logoutReason === 'manual' && path.startsWith('/onboarding/auth')) return;
        if (justUnlockedAt && Date.now() - justUnlockedAt < 10000) return;
        if (!hasPasskey || !lastPhone) return;
        if (!authContext) return;
        const { getAuth } = await loadAuth();
        const authed = !!getAuth().currentUser;
        if (cancelled || authed) return;
        if (!hasSession) {
          nav('/quick-unlock', { replace: true });
        }
      } catch {}
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, nav, authSettled, authedUserExists]);

  // Eagerly boot Firebase so downstream callers (analytics/auth) always have a default app
  useEffect(() => {
    ensureFirebaseBoot().catch(() => {});
  }, []);

  // Daily streak ping (server authoritative)
  useEffect(() => {
    let unsub: (() => void) | null = null;
    const start = async () => {
      try {
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        const auth = getAuth();
        unsub = onAuthStateChanged(auth, async (user) => {
          if (!user) return;
          if (!isCustomerRewardRoute()) return;
          const uid = user.uid;
          const key = `bw.streak.ping:${uid}`;
          const today = new Date().toISOString().slice(0, 10);
          if (streakPingKeyRef.current === `${key}:${today}`) return;
          const token = await user.getIdToken().catch(() => null);
          if (!token) return;
          try {
            const res = await fetch(`${import.meta.env.VITE_API_BASE || '/api'}/streak/ping`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              streakPingKeyRef.current = `${key}:${today}`;
              try { localStorage.setItem(key, today); } catch {}
            }
          } catch {
            /* ignore */
          }
        });
      } catch {
        /* ignore */
      }
    };
    start();
    return () => {
      if (unsub) unsub();
    };
  }, []);

  // Daily login streak + coins (local-first, once per day)
  useEffect(() => {
    let unsub: (() => void) | null = null;
    const start = async () => {
      try {
        const { getAuth, onAuthStateChanged } = await import('firebase/auth');
        const auth = getAuth();
        unsub = onAuthStateChanged(auth, async (user) => {
          if (!user) return;
          if (!isCustomerRewardRoute()) return;
          const uid = user.uid;
          const today = new Date().toISOString().slice(0, 10);
          const keyBase = `bw.login.streak:${uid}`;
          const dayKey = `${keyBase}:day`;
          const curKey = `${keyBase}:current`;
          const bestKey = `${keyBase}:best`;
          if (loginStreakKeyRef.current === `${dayKey}:${today}`) return;
          const lastDay = localStorage.getItem(dayKey);
          if (lastDay === today) {
            loginStreakKeyRef.current = `${dayKey}:${today}`;
            return;
          }
          const prevCur = Number(localStorage.getItem(curKey) || 0);
          const prevBest = Number(localStorage.getItem(bestKey) || 0);
          let nextCur = 1;
          if (lastDay) {
            const delta = daysBetween(new Date(lastDay), new Date());
            nextCur = delta === 1 ? prevCur + 1 : 1;
          }
          const nextBest = Math.max(prevBest, nextCur);
          localStorage.setItem(dayKey, today);
          localStorage.setItem(curKey, String(nextCur));
          localStorage.setItem(bestKey, String(nextBest));
          loginStreakKeyRef.current = `${dayKey}:${today}`;

          try {
            await addCoins(LOGIN_STREAK_REWARD, 'daily_streak');
            emit('bw:tokens:gain', { amount: LOGIN_STREAK_REWARD });
            toast.success(`Daily streak +${LOGIN_STREAK_REWARD} Bites`);
          } catch {
            toast.push('Daily streak earned. Coins will sync soon.');
          }
        });
      } catch {
        /* ignore */
      }
    };
    start();
    return () => {
      if (unsub) unsub();
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const wireUpdateEvents = (reg: ServiceWorkerRegistration) => {
      if (reg.waiting) setUpdateReady(true);
      reg.addEventListener('updatefound', () => {
        const worker = reg.installing;
        if (!worker) return;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            swRegRef.current = reg;
            setUpdateReady(true);
          }
        });
      });
    };
    navigator.serviceWorker.getRegistrations().then(async (regs) => {
      regs
        .filter((r) => r.active?.scriptURL.includes('firebase-messaging-sw.js'))
        .forEach((r) => r.unregister().catch(() => {}));
      const existing = regs.find((r) => r.active?.scriptURL.includes('pwa-sw.js')) || null;
      if (existing) {
        swRegRef.current = existing;
        wireUpdateEvents(existing);
        try {
          await existing.update();
        } catch (e) {
          if (import.meta.env.DEV) console.debug('[sw] update failed', e);
        }
        return;
      }
      try {
        const reg = await navigator.serviceWorker.register(SW_URL, { updateViaCache: 'none' });
        if (import.meta.env.DEV) console.debug('[sw] pwa-sw.js registered', reg.scope);
        swRegRef.current = reg;
        wireUpdateEvents(reg);
        try {
          await reg.update();
        } catch (e) {
          if (import.meta.env.DEV) console.debug('[sw] update failed', e);
        }
      } catch (e) {
        console.warn('[sw] register failed', e);
      }
    });
  }, []);

  const applySwUpdate = () => {
    const reg = swRegRef.current;
    const waiting = reg?.waiting;
    if (!waiting) return;
    const onChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onChange, { once: true });
    waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    log('[sw] controller', {
      pathname: location.pathname,
      scriptURL: navigator.serviceWorker?.controller?.scriptURL || null,
      ts: now(),
    });
    navigator.serviceWorker.ready
      .then((reg) => {
        log('[sw] ready', {
          pathname: location.pathname,
          scriptURL: reg.active?.scriptURL || null,
          scope: reg.scope,
          ts: now(),
        });
      })
      .catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const isDebug = import.meta.env.DEV || new URLSearchParams(location.search || '').get('debug') === '1';
    if (!isDebug) return;
    (window as any).__BW_HARD_REFRESH__ = async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith('bw:')) localStorage.removeItem(k);
        });
        Object.keys(sessionStorage).forEach((k) => {
          if (k.startsWith('bw:')) sessionStorage.removeItem(k);
        });
      } catch {}
      window.location.reload();
    };
  }, [location.search]);

  // Listen for SW update notifications and offer a one-click reload
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data: any = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'SW_UPDATED') {
        toast.push('New version available');
        setUpdateReady(true);
        navigator.serviceWorker.getRegistration().then((reg) => {
          swRegRef.current = reg || swRegRef.current;
        });
      } else if (data.type === 'bw:push:open') {
        const deepLink = String(data.deepLink || '');
        if (!deepLink) return;
        if (deepLink.startsWith('http')) {
          window.location.href = deepLink;
        } else {
          nav(deepLink, { replace: true });
        }
      } else if (data.type === 'bw:push:foreground') {
        const title = String(data.payload?.title || 'New update');
        const body = String(data.payload?.body || '').trim();
        toast.push(body ? `${title}: ${body}` : title);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const removeId = params.get('cart_remove') || params.get('remove');
    if (!removeId) return;
    try {
      useCart.getState().remove(removeId);
    } catch {}
    params.delete('cart_remove');
    params.delete('remove');
    const next = params.toString();
    nav(`${location.pathname}${next ? `?${next}` : ''}`, { replace: true });
  }, [location.pathname, location.search, nav]);

  useEffect(() => {
    if (cartSyncTimerRef.current) {
      window.clearTimeout(cartSyncTimerRef.current);
    }
    cartSyncTimerRef.current = window.setTimeout(async () => {
      cartSyncTimerRef.current = null;
      try {
        const { getAuth } = await loadAuth();
        const user = getAuth().currentUser;
        if (!user) return;
        const items = (cartItems || []).map((it) => ({
          id: String(it.id || '').trim(),
          name: String(it.name || '').trim(),
          qty: Number(it.qty || 0),
          updatedAt: typeof it.updatedAt === 'number' ? it.updatedAt : undefined,
        })).filter((it) => it.id && it.qty > 0);
        const payload = { items, updatedAt: Date.now() };
        const hash = JSON.stringify(payload.items);
        if (hash === cartSyncHashRef.current) return;
        cartSyncHashRef.current = hash;
        const api = await import('../lib/api');
        await api.syncCart(payload);
      } catch {}
    }, 1200);
    return () => {
      if (cartSyncTimerRef.current) {
        window.clearTimeout(cartSyncTimerRef.current);
      }
    };
  }, [cartItems]);

  useEffect(() => {
    if (!updateReady || swReloadedRef.current) return;
    if (!('serviceWorker' in navigator)) return;
    const reg = swRegRef.current;
    if (!reg?.waiting) return;
    swReloadedRef.current = true;
    const onControllerChange = () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    reg.waiting.postMessage('SKIP_WAITING');
  }, [updateReady]);

  // Hydrate profile on app boot if Firebase auth exists (no push on boot)
  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;
    const boot = async () => {
      try {
        const { getAuth, onAuthStateChanged } = await loadAuth();
        const auth = getAuth();
        const run = async (user: any) => {
          if (!user || hydratedRef.current || cancelled) return;
          hydratedRef.current = true;
          try {
            const mod = await lazyCloud();
            await mod.hydrateActiveFromCloud();
          } catch {}
        };
        unsub = onAuthStateChanged(auth, run);
        if (auth.currentUser) void run(auth.currentUser);
      } catch {}
    };
    boot();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const hasKnownAccount = () => {
    try {
      if (getActivePhone()) return true;
      if (localStorage.getItem('bw.active.phone')) return true;
      if (localStorage.getItem('bw:lastUserPhone')) return true;
      if (localStorage.getItem('bw.lastPhone')) return true;
      if (localStorage.getItem('bw.hasAccount') === '1') return true;
    } catch {}
    return false;
  };
  const hasActiveSession = () => {
    try {
      return !!sessionStorage.getItem('bw.session.phone');
    } catch {}
    return false;
  };
  const isVerifiedAuth = () => {
    try { return sessionStorage.getItem('bw.auth.verified') === '1'; } catch { return false; }
  };

  // "/" is the public QR ecosystem entry; do not auto-redirect it into the consumer app shell.
  useEffect(() => {
    return;
  }, []);

  /* Track last route (return after unlock) */
useEffect(() => {
  if (
    !isOperationalRoute(location.pathname) &&
    !location.pathname.startsWith('/onboarding') &&
    !location.pathname.startsWith('/auth') &&
    location.pathname !== '/unlock'
  ) {
    const path = location.pathname + (location.search || '');
    setLastRoute(path);
    try { sessionStorage.setItem('bw.lastRoute', path); } catch {}
  }
}, [location.pathname, location.search]);

  /* Ensure Firebase is warmed up for onboarding/auth routes */
  useEffect(() => {
      const path = location.pathname;
      const needsImmediate =
        path.startsWith('/onboarding') ||
        path.startsWith('/auth') ||
        path === '/unlock';
      if (isOperationalRoute(path)) return;
      if (needsImmediate || hasActiveSession()) {
        ensureFirebaseBoot().catch(() => {});
      }
  }, [location.pathname]);

  /* Redirect on auth change ONLY from auth entry points */
  useEffect(() => {
    const handleAuth = async () => {
      try {
        const { getAuth } = await loadAuth();
        const authedUser = getAuth().currentUser;
        if (authedUser?.phoneNumber) {
          rememberKnownAccount(authedUser.phoneNumber, { name: authedUser.displayName || undefined });
        }
        const isFullyAuthed = !!authedUser?.uid && !!authedUser?.phoneNumber;
        if (!isFullyAuthed) return;

        const path = location.pathname;
        const fromUnlock = path === '/unlock' || path === '/quick-unlock';
        const inOnboarding = path.startsWith('/onboarding');
        if (!fromUnlock || inOnboarding) return;

        // ✅ ensure push is “registered” locally when we successfully unlock
        try {
          const notify = await lazyNotify();
          await notify.initOrRefreshPushOnAuth(getActivePhone() || undefined);
        } catch {}

        const last = sessionStorage.getItem('bw.lastRoute') || getLastRoute() || '/home';
        window.setTimeout(() => nav(last, { replace: true }), 0);
      } catch {
        window.setTimeout(() => nav('/home', { replace: true }), 0);
      }
    };
  const off = on('bw:auth:changed', handleAuth);
  void handleAuth();
  return () => off();
}, [nav, location.pathname]);

  /* Known returning users should not sit on the welcome page when signed out */
  useEffect(() => {
    if (location.pathname !== '/') return;
    // Public QR root keeps its own lightweight phone/name restore flow.
    return;
  }, [location.pathname]);

  /* PERMISSION RECHECK → route to wizard (no silent prompts here) */
  useEffect(() => {
  const run = async () => {
    try {
      const { getAuth } = await loadAuth();
      const authed = !!getAuth().currentUser;
      if (authed && hasActiveSession() && getActivePhone()) {
        const cloud = await lazyCloud();
        await cloud.hydrateActiveFromCloud();
        await cloud.pushActiveToCloud();
        const tokens = await lazyTokens();
        await tokens.syncTokensFromCloud();
        try {
          const notify = await lazyNotify();
          await notify.initOrRefreshPushOnAuth(getActivePhone() || undefined);
        } catch {}
        try {
          const tasksMod = await import('../lib/tasks');
          if (typeof tasksMod.syncMissionsWithCloud === 'function') {
            await tasksMod.syncMissionsWithCloud();
          }
        } catch (err) {
          console.warn('[missions] sync skipped', err);
        }
      }
    } catch {}
  };
  run();
  const off = on('bw:auth:changed', run);
  return () => off();
}, []);

  /* ✅ NEW: if notifications flip to granted at runtime, init push */
  useEffect(() => {
    const onPermChanged = async () => {
      try {
        if (decidePerm('notifications') === 'allow' && (authedUserExists || hasKnownAccount())) {
          const notify = await lazyNotify();
          await notify.initOrRefreshPushOnAuth(getActivePhone() || undefined);
        }
        if (decidePerm('location') === 'allow' && (authedUserExists || hasKnownAccount())) {
          const today = new Date().toISOString().slice(0, 10);
          const key = 'bw.push.location.day';
          if (localStorage.getItem(key) !== today) {
            const notify = await lazyNotify();
            if (typeof notify.dispatchEngagementPush === 'function') {
              await notify.dispatchEngagementPush({
                trigger: 'location_enabled',
                category: 'location',
                title: 'Location enabled',
                body: 'We’ll show prices and ETA near you.',
                deepLink: '/availability',
                data: { source: 'location_enabled' },
              });
              localStorage.setItem(key, today);
            }
          }
        }
      } catch {}
    };
    const off = on('bw:perm:recheck', onPermChanged);
    window.addEventListener('storage', onPermChanged);
    void onPermChanged();
    return () => {
      off();
      window.removeEventListener('storage', onPermChanged);
    };
  }, []);

  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;

    const boot = async () => {
      try {
        const mod = await lazyReturn();
        if (cancelled) return;
        stop = mod.initReturnListener();
      } catch (err) {
        console.error('Return listener failed', err);
      }
    };

    boot();

    return () => {
      cancelled = true;
      stop?.();
    };
  }, []);

  /* Cloud hydrate/push */
  useEffect(() => {
    const run = async () => {
      try {
        if (authedUserExists && getActivePhone()) {
          const cloud = await lazyCloud();
          await cloud.hydrateActiveFromCloud();
          await cloud.pushActiveToCloud();
        }
      } catch {}
    };
    run();
    const off = on('bw:auth:changed', run);
    return () => off();
  }, []);

  /* Idle logout */
  useEffect(() => {
    const inAuthContext = () => {
      const p = location.pathname;
      if (isOperationalRoute(p)) return true;
      return p.startsWith('/onboarding') || p.startsWith('/auth') || p === '/unlock';
    };
    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const scheduleLogout = () => {
      clearTimer();
      // ⛔️ Do not arm an idle logout if there is no active session OR we're inside onboarding/auth
      if (!IDLE_MS) return;
      if (!authedUserExists || !hasKnownAccount() || inAuthContext()) return;
      timerRef.current = window.setTimeout(async () => {
        // 1. mark reason + remember phone for prefill
        const lastPhone =
          sessionStorage.getItem('bw.session.phone') || '' ;

  try {
    sessionStorage.setItem('bw.logoutReason', 'idle');
    if (lastPhone) {
      rememberKnownAccount(lastPhone);
    }

    // 2. hard sign out of Firebase so tokens are dead
    try {
      const { getAuth, signOut } = await loadAuth();
      await signOut(getAuth());
    } catch {
      // ignore network / already-signed-out errors
    }

    // 3. clear in-tab auth hints
    clearVolatileAuthState();
    try { localStorage.removeItem('bw.idle.until'); } catch {}

    // 4. broadcast auth change (cart badges, balances, etc.)
    try { window.dispatchEvent(new Event('bw:auth:changed')); } catch {}
    try { window.dispatchEvent(new Event('bw:boot:invalidated')); } catch {}
    try {
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: 'bw.session.phone',
          newValue: null as any,
        }) as any
      );
    } catch {}
  } finally {
    // 5. go to /unlock
    window.setTimeout(() => nav('/unlock', { replace: true }), 0);
  }
}, IDLE_MS);
    };
    const onActivity = () => {
      if (document.visibilityState === 'visible') scheduleLogout();
    };
    // initial arm/skip
    scheduleLogout();
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('mousemove', onActivity, opts);
    window.addEventListener('keydown', onActivity, opts);
    window.addEventListener('click', onActivity, opts);
    window.addEventListener('scroll', onActivity, opts);
    window.addEventListener('wheel', onActivity, opts);
    window.addEventListener('pointerdown', onActivity, opts);
    window.addEventListener('touchstart', onActivity, opts);
    window.addEventListener('visibilitychange', onActivity, opts);
    // reschedule when the session phone changes
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key === 'bw.session.phone') scheduleLogout();
    };
    window.addEventListener('storage', onStorage);
    // cleanup
    return () => {
      clearTimer();
      window.removeEventListener('mousemove', onActivity, opts);
      window.removeEventListener('keydown', onActivity, opts);
      window.removeEventListener('click', onActivity, opts);
      window.removeEventListener('scroll', onActivity, opts);
      window.removeEventListener('wheel', onActivity, opts);
      window.removeEventListener('pointerdown', onActivity, opts);
      window.removeEventListener('touchstart', onActivity, opts);
      window.removeEventListener('visibilitychange', onActivity, opts);
      window.removeEventListener('storage', onStorage);
    };
  }, [nav, location.pathname]);

  /* Feed boot */
  useEffect(() => {
    let stop: (() => void) | null = null;
    (async () => {
      try {
        if (FEED_KIND === 'dummy' && ALLOW_DUMMY_FEED) {
          console.warn('[feed] Dummy feed enabled for development');
          const m = await import('../lib/feed/DummyAdapter');
          stop = m.startDummyFeed();
        } else if (import.meta.env.DEV) {
          console.debug('[feed] using crawl-backed product data');
        }
      } catch (err) {
        console.error('Feed init failed', err);
      }
    })();
    return () => {
      stop?.();
    };
  }, []);

  /* Offline/online detection with toasts */
  useEffect(() => {
        const handle = () => {
          const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
          setOffline(isOffline);
          if (isOffline) {
            toast.error('You are offline. We will retry when back online.');
          } else {
            toast.success('Back online');
          }
        };
    window.addEventListener('online', handle);
    window.addEventListener('offline', handle);
    handle();
    return () => {
      window.removeEventListener('online', handle);
      window.removeEventListener('offline', handle);
    };
  }, []);

  /* Tasks engine */
  useEffect(() => {
    let cleanup: (() => void) | null = null;
    let cancelled = false;

    const boot = async () => {
      try {
        const mod = await import('../lib/TaskEngine');
        if (cancelled) return;
        cleanup = mod.startTaskEngine();
      } catch (err) {
        console.error('Task engine failed to start', err);
      }
    };

    let idleId: number | null = null;
    const idleCb = () => {
      idleId = null;
      boot();
    };

    const win = window as Window & typeof globalThis;
    if ('requestIdleCallback' in win) {
      idleId = (win as any).requestIdleCallback(idleCb, { timeout: 2000 });
    } else {
      idleId = window.setTimeout(idleCb, 1200);
    }

    return () => {
      cancelled = true;
      if (idleId != null) {
        if ('cancelIdleCallback' in win && typeof (win as any).cancelIdleCallback === 'function') {
          (win as any).cancelIdleCallback(idleId);
        } else {
          win.clearTimeout(idleId);
        }
      }
      cleanup?.();
    };
  }, []);

  /* Passive live pings */
  useEffect(() => {
    let stop: (() => void) | null = null;
    let cancelled = false;
    let timer: number | null = null;

    const boot = async () => {
      try {
        const mod = await import('../lib/location');
        if (cancelled) return;
        stop = mod.startLiveLocationWatcher(20000);
      } catch (err) {
        console.error('Live location watcher failed to start', err);
      }
    };

    timer = window.setTimeout(boot, 1500);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      stop?.();
    };
  }, []);

  /* Auto-start guided tour once */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const path = location.pathname;
      if (path !== '/home') return;
      if (!authedUserExists) return;

      const tourState = useTour.getState();
      if (tourState.active || tourState.steps.length > 0) return;
      if (!shouldAutoStartTour()) return;

      const steps = [
        {
          id: 'addr',
          selector: '#nav-address',
          title: 'Start with your delivery area',
          body: 'Confirm or change your address first so BiteWise shows the right restaurants, prices, and ETA.',
          position: 'bottom' as const,
        },
        {
          id: 'mic',
          selector: '#nav-mic',
          title: 'Search fast by voice',
          body: 'Tap the mic to quickly search dishes nearby instead of typing through the whole menu.',
          position: 'bottom' as const,
        },
        {
          id: 'menu',
          selector: '#nav-menu',
          title: 'Everything else lives here',
          body: 'Come here later for settings, notifications, referrals, rewards, and account options.',
          position: 'bottom' as const,
        },
      ];

      const start = () => {
        const available = steps.filter((s) => document.querySelector(s.selector));
        if (!available.length) return;
        tourState.setSteps(available);
        tourState.start();
      };

      const timer = window.setTimeout(start, 900);
      return () => window.clearTimeout(timer);
    } catch {
      // fail-safe: do nothing if tour cannot start
    }
  }, [location.pathname, authedUserExists]);

  return (
    <MissionStatsProvider>
      <div className="app-shell">
        <OfflineBanner offline={offline} />
        {updateReady ? (
          <div className="fixed left-1/2 top-4 -translate-x-1/2 z-[130]">
            <div className="rounded-xl border border-emerald-200 bg-white/95 text-slate-900 shadow px-4 py-2 flex items-center gap-3">
              <div className="text-sm font-semibold">New version available</div>
              <button
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                onClick={() => {
                  setUpdateReady(false);
                  const reg = swRegRef.current;
                  if (reg?.waiting) {
                    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
                  }
                  window.location.reload();
                }}
              >
                Refresh
              </button>
            </div>
          </div>
        ) : null}
        <div className="app-shell__aurora" aria-hidden="true" />
        <div className="app-shell__noise" aria-hidden="true" />
        <main className="app-shell__content">
          <Suspense fallback={null}>
            <RouteTransition routeKey={location.pathname}>
              <Outlet />
            </RouteTransition>
          </Suspense>
        </main>
        <AppFooter />
        <ConfettiBurst />
        <RewardHost />
        <ToastHost />
        <TourOverlay />
        <OfflineTooltip offline={offline} />
        <Suspense fallback={<div className="h-4 w-full" />}>
          <BannerHost />
        </Suspense>
      </div>
    </MissionStatsProvider>
  );
}
